import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { SERVICE_CATEGORIES } from '../../../config/ieumConfig';
import Pagination from '../../../components/common/Pagination';
import { db } from '../../../firebase/config';
import { formatUserName } from '../../../utils/userName';
import { collection, getDocs, doc, getDoc, orderBy, query } from 'firebase/firestore';

const TYPE_META = {
  conversion: { label: '전환수수료', color: '#3B82F6' },
  referral: { label: '추천수수료', color: '#8B5CF6' },
  consulting: { label: '상담수수료', color: '#F59E0B' },
};

const STATUS_META = {
  unsettled: { label: '미정산', color: '#EF4444' },
  settled: { label: '정산완료', color: '#22C55E' },
};

const STATUS_ORDER = { unsettled: 0, settled: 1 };

// 세부 유형 라벨 매핑 (카테고리별 공통)
const SUB_TYPE_LABELS = {
  // moving
  normal: '일반이사', semi_pack: '반포장이사', pack: '포장이사', van: '용달이사',
  // cleaning
  basic: '기본 입주청소', deep: '딥클리닝', both: '둘 다',
  // internet
  internet_only: '인터넷 단독', internet_tv: '인터넷+TV',
  // interior
  value: '가성비', modern: '모던', simple: '심플', custom: '직접상담',
};

const PER_PAGE = 20;

const RealtorCommission = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const fetchData = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'commissions'), orderBy('created_at', 'desc'));
      const snap = await getDocs(q);
      const enriched = await Promise.all(
        snap.docs.map(async (d) => {
          const c = d.data();
          let realtorName = '-';
          let loginId = '-';
          if (c.realtor_id) {
            try {
              const [rSnap, rDocSnap] = await Promise.all([
                getDoc(doc(db, 'users', c.realtor_id)),
                getDoc(doc(db, 'realtors', c.realtor_id)),
              ]);
              if (rSnap.exists()) {
                const rd = rSnap.data();
                const rDoc = rDocSnap.exists() ? rDocSnap.data() : {};
                realtorName = formatUserName({
                  ...rd,
                  business_name: rDoc.businessName || rDoc.business_name || rd.business_name || '',
                });
                loginId = rd.loginId || rd.email || rd.phone || '-';
              }
            } catch { /* skip */ }
          }

          let customerName = '-';
          let subType = '';
          if (c.service_request_id) {
            try {
              const srSnap = await getDoc(doc(db, 'service_requests', c.service_request_id));
              if (srSnap.exists()) {
                const sr = srSnap.data();
                subType = sr.moving_type || sr.cleaning_type || sr.internet_type || sr.interior_style || '';
                const custId = sr.customer_id;
                if (custId) {
                  const cSnap = await getDoc(doc(db, 'customers', custId));
                  if (cSnap.exists()) customerName = cSnap.data().name || '-';
                }
              }
            } catch { /* skip */ }
          }

          return {
            id: d.id,
            loginId,
            realtorName,
            customerName,
            category: c.category || '',
            subType,
            type: c.type || 'conversion',
            amount: c.amount || 0,
            withdrawnAmount: c.withdrawn_amount || 0,
            status: c.is_settled ? 'settled' : 'unsettled',
            date: (() => {
              const d = c.created_at?.toDate?.();
              if (!d) return '';
              const pad = (n) => String(n).padStart(2, '0');
              return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
            })(),
          };
        })
      );
      setData(enriched);
    } catch (err) {
      console.error('수수료 로딩 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = useMemo(() => {
    const list = data.filter((d) => {
      if (typeFilter !== 'all' && d.type !== typeFilter) return false;
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      return true;
    });
    const getVal = (d) => {
      if (sortKey === 'realtorName') return (d.realtorName || '').toLowerCase();
      if (sortKey === 'customerName') return (d.customerName || '').toLowerCase();
      if (sortKey === 'amount') return d.amount || 0;
      if (sortKey === 'status') return STATUS_ORDER[d.status] ?? 99;
      return d.date || '';
    };
    return [...list].sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, typeFilter, statusFilter, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'date' || key === 'amount' ? 'desc' : 'asc'); }
    setPage(1);
  };
  const sortInd = (key) => (
    <SortInd $active={sortKey === key}>{sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '▲▼'}</SortInd>
  );

  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));

  const totalAmount = data.reduce((sum, d) => sum + d.amount, 0);
  const settledAmount = data.filter((d) => d.status === 'settled').reduce((sum, d) => sum + d.amount, 0);
  const unsettledAmount = data.filter((d) => d.status === 'unsettled').reduce((sum, d) => sum + d.amount, 0);

  const handleExport = () => {
    const rows = [
      ['로그인 ID', '부동산', '고객', '유형', '카테고리', '금액', '출금요청', '상태', '일시'],
      ...filtered.map((d) => [
        d.loginId,
        d.realtorName,
        d.customerName,
        TYPE_META[d.type]?.label || '',
        SERVICE_CATEGORIES[d.category]?.label || '',
        d.amount,
        d.withdrawnAmount,
        STATUS_META[d.status]?.label || '',
        d.date,
      ]),
    ];
    const csv = rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `realtor_commission_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const TypeText = ({ type }) => {
    const t = TYPE_META[type];
    if (!t) return <span>-</span>;
    return <span style={{ color: t.color, fontWeight: 500 }}>{t.label}</span>;
  };
  const StatusText = ({ status }) => {
    const s = STATUS_META[status];
    if (!s) return <span>-</span>;
    return <span style={{ color: s.color, fontWeight: 500 }}>{s.label}</span>;
  };

  return (
    <Container>
      <TopRow>
        <Title>수수료 관리</Title>
        <TopActions>
          <TopBtn onClick={handleExport}>현재 목록 내보내기 (CSV)</TopBtn>
          <TopBtn onClick={fetchData}>새로고침</TopBtn>
        </TopActions>
      </TopRow>

      <StatsRow>
        <StatCard>
          <StatLabel>이번달 발생 수수료</StatLabel>
          <StatValue>{totalAmount.toLocaleString()}원</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>정산 완료</StatLabel>
          <StatValue $color="#22C55E">{settledAmount.toLocaleString()}원</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>미정산</StatLabel>
          <StatValue $color="#F59E0B">{unsettledAmount.toLocaleString()}원</StatValue>
        </StatCard>
      </StatsRow>

      <FilterRow>
        <Select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
          <option value="all">유형 전체</option>
          {Object.entries(TYPE_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </Select>
        <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="all">상태 전체</option>
          {Object.entries(STATUS_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </Select>
        <CountText>총 {filtered.length}건</CountText>
      </FilterRow>

      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <Th>로그인 ID</Th>
              <Th $sortable $active={sortKey === 'realtorName'} onClick={() => toggleSort('realtorName')}>부동산{sortInd('realtorName')}</Th>
              <Th $sortable $active={sortKey === 'customerName'} onClick={() => toggleSort('customerName')}>고객{sortInd('customerName')}</Th>
              <Th>유형</Th>
              <Th $sortable $active={sortKey === 'amount'} onClick={() => toggleSort('amount')} style={{ textAlign: 'right' }}>금액{sortInd('amount')}</Th>
              <Th style={{ textAlign: 'right' }}>출금요청</Th>
              <Th $sortable $active={sortKey === 'status'} onClick={() => toggleSort('status')}>상태{sortInd('status')}</Th>
              <Th $sortable $active={sortKey === 'date'} onClick={() => toggleSort('date')}>일시{sortInd('date')}</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><Td colSpan={8} style={{ textAlign: 'center', padding: 48 }}>로딩 중...</Td></tr>
            ) : paged.length === 0 ? (
              <tr><Td colSpan={8} style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>발생한 수수료가 없습니다.</Td></tr>
            ) : paged.map((d) => (
              <Tr key={d.id}>
                <Td style={{ color: '#6B7280' }}>{d.loginId}</Td>
                <Td>{d.realtorName}</Td>
                <Td>{d.customerName}</Td>
                <Td>
                  <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 2 }}>
                    {[SERVICE_CATEGORIES[d.category]?.label, SUB_TYPE_LABELS[d.subType]].filter(Boolean).join(' · ') || '-'}
                  </div>
                  <TypeText type={d.type} />
                </Td>
                <Td style={{ textAlign: 'right', fontWeight: 600 }}>{d.amount.toLocaleString()}원</Td>
                <Td style={{ textAlign: 'right', color: '#6B7280' }}>{d.withdrawnAmount.toLocaleString()}원</Td>
                <Td><StatusText status={d.status} /></Td>
                <Td>{d.date || '-'}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>

      <PageFoot>
        <PageInfo>페이지 {page} / {totalPages} · 총 {filtered.length}건</PageInfo>
        <Pagination current={page} total={totalPages} onChange={setPage} />
      </PageFoot>
    </Container>
  );
};

export default RealtorCommission;

// --- Styled ---
const Container = styled.div``;
const TopRow = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;`;
const Title = styled.h1`font-size:18px;font-weight:600;margin:0;`;
const TopActions = styled.div`display:flex;gap:6px;`;
const TopBtn = styled.button`padding:7px 12px;border:1px solid ${({theme})=>theme.colors.border};background:#fff;border-radius:${({theme})=>theme.radius.sm};font-size:12px;cursor:pointer;&:hover{background:#F9FAFB;}`;

const StatsRow = styled.div`display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:16px;`;
const StatCard = styled.div`background:#fff;padding:18px 20px;border-radius:${({theme})=>theme.radius.md};box-shadow:${({theme})=>theme.shadow.sm};`;
const StatLabel = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};margin-bottom:6px;`;
const StatValue = styled.div`font-size:20px;font-weight:700;color:${({$color})=>$color||'#1F2937'};`;

const FilterRow = styled.div`display:flex;gap:8px;align-items:center;margin-bottom:12px;`;
const Select = styled.select`padding:8px 12px;border:1px solid ${({theme})=>theme.colors.border};border-radius:${({theme})=>theme.radius.sm};font-size:13px;background:#fff;&:focus{outline:none;border-color:${({theme})=>theme.colors.primary};}`;
const CountText = styled.span`margin-left:auto;font-size:13px;color:${({theme})=>theme.colors.textSecondary};`;

const TableWrapper = styled.div`overflow-x:auto;background:#fff;border-radius:${({theme})=>theme.radius.md};box-shadow:${({theme})=>theme.shadow.sm};`;
const Table = styled.table`width:100%;border-collapse:collapse;`;
const Th = styled.th`
  background:#F9FAFB;
  padding:12px 14px;
  text-align:left;
  font-size:12px;
  font-weight:500;
  color:${({theme,$active})=>$active?theme.colors.primary:theme.colors.textSecondary};
  border-bottom:1px solid ${({theme})=>theme.colors.border};
  white-space:nowrap;
  ${({$sortable,theme})=>$sortable&&`
    cursor:pointer;
    user-select:none;
    &:hover { color: ${theme.colors.primary}; }
  `}
`;
const SortInd = styled.span`
  display:inline-block;
  margin-left:6px;
  color:${({theme,$active})=>$active?theme.colors.primary:'#C0C7D1'};
  font-size:10px;
  line-height:1;
  vertical-align:middle;
`;
const Tr = styled.tr`&:not(:last-child) td{border-bottom:1px solid ${({theme})=>theme.colors.borderLight};}`;
const Td = styled.td`padding:12px 14px;font-size:13px;color:${({theme})=>theme.colors.text};white-space:nowrap;`;

const PageFoot = styled.div`display:flex;align-items:center;justify-content:space-between;`;
const PageInfo = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};`;
